import type { ReactNode } from 'react';
import { isApiError, isNetworkError } from '../../api/client';

interface ErrorStateProps {
  readonly title?: string;
  readonly description?: string;
  readonly error?: unknown;
  readonly onRetry?: () => void;
  readonly action?: ReactNode;
}

/**
 * `null` when there is nothing to classify — a caller that passed no
 * `error` has no wire code, and inventing one (this returned a literal
 * `unknown_error`, which is not a `WireErrorCode`) printed a fabricated
 * diagnosis as user-visible text. Task 301B QA.
 */
function describeError(error: unknown): { code: string; detail: string } | null {
  if (isApiError(error)) {
    return { code: error.code, detail: error.body.detail ?? '' };
  }
  if (isNetworkError(error)) {
    return { code: 'network_error', detail: error.message };
  }
  return null;
}

/**
 * Generic error state matching shell.css `.state-error` plus an optional
 * retry button. The frontend never renders raw provider / Postgres
 * messages — only the typed wire `code` (and the request_id when present
 * on a 500 response, per `docs/10 §5.2`), and only when there is one.
 */
export function ErrorState({
  title = 'تعذر تحميل البيانات',
  description = 'حدث خطأ أثناء تحميل هذا القسم. حاول مجدداً، وإذا تكررت المشكلة تواصل مع المسؤول.',
  error,
  onRetry,
  action,
}: ErrorStateProps) {
  const described = describeError(error);
  const requestId =
    isApiError(error) && error.code === 'internal_error' ? error.body.request_id : undefined;
  return (
    <div className="state state-error">
      <div className="glyph">
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {described ? (
        <p className="tiny" style={{ marginTop: 4 }}>
          {described.code}
          {described.detail ? ` · ${described.detail}` : ''}
          {requestId ? ` · req:${requestId.slice(0, 8)}` : ''}
        </p>
      ) : null}
      {action ??
        (onRetry ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
            إعادة المحاولة
          </button>
        ) : null)}
    </div>
  );
}
